import type { InfiniteData } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEqual } from "lodash-es";

import { useApi } from "@/api/hooks/useApi";
import type {
  AdminNotificationDtoPaginationResultDto,
  AdminNotificationStatusDto,
  HttpResponse,
  ResidentNotificationResultDto,
  UnreadStatusDto,
} from "@/api/types";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

const useMarkNotificationsAsRead = (
  data: InfiniteData<AdminNotificationDtoPaginationResultDto> | undefined,
  query: ApiQueryParams<"getAdminNotificationsV1">,
) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteAdminNotificationsReadByIdV1(id),
    onMutate(id) {
      const newData = structuredClone(data);
      if (newData?.pages) {
        for (const page of newData.pages) {
          for (const item of page.items) {
            if (item.id === id) {
              item.isRead = true;
              queryClient.setQueryData(QUERY_KEYS.ADMIN_NOTIFICATION_LIST(projectId, query), newData);
              queryClient.setQueryData<HttpResponse<AdminNotificationStatusDto>>(
                QUERY_KEYS.ADMIN_NOTIFICATION_STATUS(projectId),
                (oldResponse) =>
                  oldResponse === undefined
                    ? undefined
                    : {
                        ...oldResponse,
                        data: {
                          amount: oldResponse.data.amount > 0 ? oldResponse.data.amount - 1 : 0,
                          hasUnread: oldResponse.data.amount - 1 > 0,
                        },
                      },
              );

              return;
            }
          }
        }
      }
    },
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ADMIN_NOTIFICATION_LIST(projectId),
        predicate: (x) => {
          const key = x.queryKey as ReturnType<(typeof QUERY_KEYS)["ADMIN_NOTIFICATION_LIST"]>;
          const queryValue = key[2];

          return !isEqual(queryValue, query);
        },
      });
    },
    onError() {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ADMIN_NOTIFICATION_STATUS(projectId),
      });
    },
  });
};

const useMarkAllNotificationAsRead = (
  data: InfiniteData<AdminNotificationDtoPaginationResultDto> | undefined,
  query: ApiQueryParams<"getAdminNotificationsV1">,
) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.deleteAdminNotificationsReadV1(),
    onMutate() {
      const newData = structuredClone(data);
      if (newData?.pages) {
        for (const page of newData.pages) {
          for (const item of page.items) {
            item.isRead = true;
          }
        }
      }
      queryClient.setQueryData(QUERY_KEYS.ADMIN_NOTIFICATION_LIST(projectId, query), newData);
      queryClient.setQueryData<HttpResponse<AdminNotificationStatusDto>>(
        QUERY_KEYS.ADMIN_NOTIFICATION_STATUS(projectId),
        (oldResponse) =>
          oldResponse === undefined
            ? undefined!
            : {
                ...oldResponse,
                data: {
                  amount: 0,
                  hasUnread: false,
                },
              },
      );
    },
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ADMIN_NOTIFICATION_LIST(projectId),
        predicate: (x) => {
          const key = x.queryKey as ReturnType<(typeof QUERY_KEYS)["ADMIN_NOTIFICATION_LIST"]>;
          const queryValue = key[2];

          return !isEqual(queryValue, query);
        },
      });
    },
    onError() {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ADMIN_NOTIFICATION_STATUS(projectId),
      });
    },
  });
};

export const adminNotificationMutations = {
  useMarkNotificationsAsRead,
  useMarkAllNotificationAsRead,
};

const useMarkResidentNotificationAsRead = (query: ApiQueryParams<"getResidentNotificationsV3">) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.putResidentNotificationsReadV1(id),
    onMutate(id) {
      let didMarkAsRead = false;
      queryClient.setQueryData<InfiniteData<ResidentNotificationResultDto>>(
        QUERY_KEYS.RESIDENT_NOTIFICATION_LIST(projectId, query),
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          const newData = structuredClone(currentData);
          const notification = newData.pages.flatMap((page) => page.notifications.items).find((item) => item.id === id);

          if (!notification || notification.read) {
            return currentData;
          }

          notification.read = true;
          didMarkAsRead = true;

          return newData;
        },
      );

      if (!didMarkAsRead) {
        return;
      }

      queryClient.setQueryData<HttpResponse<UnreadStatusDto>>(
        QUERY_KEYS.RESIDENT_NOTIFICATION_STATUS(projectId),
        (oldResponse) =>
          oldResponse === undefined
            ? undefined
            : {
                ...oldResponse,
                data: {
                  ...oldResponse.data,
                  unreadNotifications: Math.max(0, oldResponse.data.unreadNotifications - 1),
                },
              },
      );
    },
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.RESIDENT_NOTIFICATION_LIST(projectId),
        predicate: (x) => {
          const key = x.queryKey as ReturnType<(typeof QUERY_KEYS)["RESIDENT_NOTIFICATION_LIST"]>;

          return !isEqual(key[2], query);
        },
      });
    },
    onError() {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.RESIDENT_NOTIFICATION_LIST(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.RESIDENT_NOTIFICATION_STATUS(projectId),
      });
    },
  });
};

export const residentNotificationMutations = {
  useMarkNotificationAsRead: useMarkResidentNotificationAsRead,
};
